import { useRef, useState } from 'react';
import { archivePath } from '../utils/url';
import { languages } from '../config/languages';

const ERROR_ID = 'ukwa-url-search-error';

const inputResetStyle = {
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  appearance: 'none',
  boxSizing: 'border-box',
};

// Localised microcopy. English is canonical; cy is the reviewed Welsh
// translation (Welsh translations August 2026). Other languages fall back to en.
const COPY = {
  en: { label: 'Search the archive by URL', hint: 'For example, https://www.bl.uk', button: 'Search' },
  cy: { label: "Chwilio'r archif yn ôl URL", hint: 'Er enghraifft, https://www.bl.uk', button: 'Chwilio' },
};

const buttonResetStyle = {
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  appearance: 'none',
  WebkitTapHighlightColor: 'transparent',
};

export default function UrlSearchBox({ theme, lang }) {
  const [url, setUrl] = useState('');
  const [invalid, setInvalid] = useState(false);
  const inputRef = useRef(null);
  const isDark = theme === 'dark';
  const copy = COPY[lang] ?? COPY.en;
  const ui = (languages[lang] ?? languages.en).ui;

  const handleSearch = (e) => {
    e.preventDefault();
    const href = archivePath(url);
    if (href) {
      // Forward to the archive's wayback-style lookup for all captures.
      window.location.href = href;
    } else {
      // Inline, programmatically associated error instead of window.alert()
      // (WCAG 3.3.1 Error Identification, 3.3.3 Error Suggestion, 4.1.3).
      setInvalid(true);
      inputRef.current?.focus();
    }
  };

  return (
    <div className="my-6 w-full px-4 sm:px-5 py-4 rounded-xl bg-gradient-to-r from-blue-700 to-blue-600 dark:from-blue-900/80 dark:via-blue-800/50 dark:to-dark-900 border border-blue-800/30 dark:border-dark-700/50 shadow-sm">
      <label
        htmlFor="ukwa-url-search-input"
        className="block text-sm font-semibold mb-3 text-white"
      >
        {copy.label}
      </label>
      <form
        onSubmit={handleSearch}
        noValidate
        className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full"
      >
        <input
          id="ukwa-url-search-input"
          type="text"
          inputMode="url"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          ref={inputRef}
          value={url}
          onChange={(e) => { setUrl(e.target.value); if (invalid) setInvalid(false); }}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? ERROR_ID : undefined}
          // The visible <label> above names the field (Report2 OA 44); the
          // placeholder only shows the expected format. Placeholder ≥ 7:1 in both
          // themes (gray-600 on white ≈ 7.6:1, dark-300 on dark-800 ≈ 10.5:1).
          placeholder={copy.hint}
          style={inputResetStyle}
          className={`flex-1 min-w-0 w-full px-4 py-2.5 rounded-lg border text-sm leading-normal transition-colors ${
            isDark
              ? 'border-dark-600 bg-dark-800 text-dark-100 placeholder-dark-300 focus:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-300'
              : 'border-blue-300 bg-white text-gray-800 placeholder-gray-600 focus:border-blue-900 focus:outline-none focus:ring-2 focus:ring-white focus:shadow-[inset_0_0_0_1px_#1e3a8a]'
          }`}
        />
        <button
          type="submit"
          style={buttonResetStyle}
          className="shrink-0 px-6 py-2.5 rounded-lg font-medium text-sm transition-colors cursor-pointer bg-white text-blue-700 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-700 dark:focus:ring-offset-dark-900 dark:bg-blue-700 dark:text-white dark:hover:bg-blue-600"
        >
          {copy.button}
        </button>
      </form>
      {invalid && (
        // White panel so the red text is ≥ 4.5:1 on the blue gradient in every
        // theme; the icon + "Error:" prefix mean it is not conveyed by colour alone.
        // A <div> and a plain bold <span>: on the home page this box sits inside the
        // Markdown content, where .prose-dark p / strong would repaint the text
        // near-white on this white panel (WCAG 1.4.3).
        <div
          id={ERROR_ID}
          role="alert"
          className="mt-3 flex items-start gap-2 rounded-lg bg-white px-3 py-2 text-sm font-medium text-red-800"
        >
          <svg aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-4a1 1 0 00-1 1v3a1 1 0 102 0V7a1 1 0 00-1-1zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
          <span><span className="font-bold">{ui.errorPrefix}</span> {ui.urlInvalid}</span>
        </div>
      )}
    </div>
  );
}
